/**
 * RelationshipPanel
 * -----------------
 * 
 * June 2026
 */

import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { ListBox } from 'primereact/listbox';

const relWarnings = {
    'resources': function(newAmount, resourceName) { return newAmount <= 0 ? `${resourceName} - amount requested must be positive` : null }
}

/**
 * ActionPanel sub-component for editing an action element's relationships with resources.
 * 
 * @param {Object} config                   - Configuration object for the simulation model. 
 * @param {Function} setConfig              - Setter for config object.
 * @param {Object} action                   - Attributes for action element being edited.
 * @param {Object} configWarnings           - Verification warnings for entity types.
 * @param {Function} updateActionWarnings   - Setter for verification warnings.
 */
function RelationshipPanel({ config, setConfig, action, selectedResGid, setResGid, configWarnings, updateActionWarnings }) {
    if (Object.keys(action.resources).length === 0) return null;

    const selectedRel = action.resources?.[selectedResGid] ?? null;

    /**
     * Updates the action's relationship with a resource.
     * @param {String} newRel       - Updated relationship (Seize or Consume).
     * @param {String} actionGid    - Action's identifier.
     * @param {String} resourceGid  - Resource's identifier.
     */
    const handleResourceRelationship = (newRel, actionGid, resourceGid) => {
        const action = config.actions[actionGid];
        const resource = action.resources[resourceGid];

        const updatedResource = {...resource, rel: newRel};
        const updatedAction = {...action, resources: {...action.resources, [resourceGid]: updatedResource}};
        const newConfig = {...config, actions: {...config.actions, [actionGid]: updatedAction}};
        setConfig(newConfig);
    }

    /**
     * Updates the action's request amount for a resource.
     * 
     * @param {Number} newAmount    - Updated request amount.
     * @param {String} actionGid    - Action's identifier.
     * @param {String} resourceGid  - Resource's identifier.
     */
    const handleResourceAmount = (newAmount, actionGid, resourceGid) => {
        updateActionWarnings(actionGid, 'resources', relWarnings['resources'](newAmount, config.resources[resourceGid].name));
        
        const action = config.actions[actionGid];
        const resource = action.resources[resourceGid];
    

        const updatedResource = {...resource, request_amount: newAmount};
        const updatedAction = {...action, resources: {...action.resources, [resourceGid]: updatedResource}};
        const newConfig = {...config, actions: {...config.actions, [actionGid]: updatedAction}};
        setConfig(newConfig);
    }

    return (
        <div className="flex gap-4" style={{ display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "1.5rem" }}>
            <ListBox value={selectedResGid} onChange={(event) => setResGid(event.value)} options={Object.entries(action.resources || {}).map(([gidKey, rel]) => ({label: config.resources[gidKey].name, value: gidKey}))} optionLabel="label" optionValue="value" style={{ width: "150px", maxHeight: '200px', overflowY: 'auto' }}/>
            <div style={{ minWidth: "260px"}}>
                {selectedRel &&
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.4rem' }}>
                            <label style={{ marginRight: '1rem'}}>Relationship:</label>
                            <Dropdown value={selectedRel.rel} onChange={(event) => handleResourceRelationship(event.target.value, action.gid, selectedResGid)} options={["Seize", "Consume"]} style={{ width: '140px' }} panelStyle={{ fontSize: "0.9rem"}} pt={{ input: { style: { fontSize: "0.9rem" }, }, }}/>
                        </div>
                        <Divider/>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <label style={{ marginRight: '1rem'}}>Amount Requested:</label>
                            <InputNumber value={selectedRel.request_amount} onValueChange={(event) => handleResourceAmount(event.target.value, action.gid, selectedResGid)} placeholder="Enter value" min={0} invalid={!!configWarnings.actions?.[action.gid]?.resources} inputStyle={{ width: '110px' }}/>
                        </div>
                    </>
                }
            </div>
        </div>
    )
}

export default RelationshipPanel;